import { useEffect, useRef, useState } from 'react'

/**
 * `true` while any scroll event has fired in the last `settleMs`; flips back
 * to `false` after the user stops scrolling for that window.
 *
 * Listens at the `window` level with capture, so it picks up scroll events
 * on any nested scroll container (the editor sits inside a scrollable
 * viewport on every chrome layout).
 *
 * Use it to dismiss anchored popups whose anchor is a virtual element
 * (e.g. `getCursorTrackingAnchorEl`). Popper.js cannot auto-listen for
 * scroll on a virtual anchor's ancestors, so the popup otherwise stays
 * pinned to its initial viewport coordinates while the document scrolls
 * out from under it. Gating the popup on `!isScrolling` makes it
 * disappear as the user scrolls and reappear once the page settles.
 */
export const useIsScrolling = (settleMs = 250): boolean => {
  const [isScrolling, setIsScrolling] = useState(false)
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolling(true)
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        setIsScrolling(false)
        timeoutRef.current = null
      }, settleMs)
    }

    window.addEventListener('scroll', handleScroll, { capture: true, passive: true })
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
      window.removeEventListener('scroll', handleScroll, { capture: true })
    }
  }, [settleMs])

  return isScrolling
}
